import { Component, computed, inject, input, output } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { dsDtos } from '@vectronic/openApi-admin';
import {
  DeviceTypeParameterConfigWithAvailableListAndParameter,
  ParameterFormatService,
} from '@vectronic/util';

@Component({
  selector: 'vectronic-parameter-visibility-flags-config',
  templateUrl: './parameter-visibility-flags-config.component.html',
  styleUrls: ['./parameter-visibility-flags-config.component.scss'],
  imports: [MatFormField, MatLabel, MatSelect, MatOption, MatIcon, MatTooltip, MatIconButton],
})
export class ParameterVisibilityFlagsConfigComponent {
  parameterFormatService = inject(ParameterFormatService);
  config = input<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  value = input<string>();
  disabled = input(false);
  readonly = input<boolean>();
  resetToFactoryTooltip = input<string>();
  subsystems = input<dsDtos.Subsystem[]>();

  sortedSubsystems = computed(() => {
    const list = this.subsystems();
    return list
      ?.slice()
      .sort((a, b) => a.identifier - b.identifier)
      .filter((s) => s.identifier <= 20);
  });

  hideLabel = input<boolean>();
  hasClose = input(false);
  showInfo = input(true);
  hasDelete = input(true);
  hasUndo = input(false);
  showReset = input<boolean>();

  configClose = output();
  parameterChange = output<{
    config: DeviceTypeParameterConfigWithAvailableListAndParameter;
    value: string;
  }>();
  parameterUndo = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  resetToFactory = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();

  selectedSubsystemIdentifiers = computed(() => {
    const visibilityFlags = this.value() || '0';
    const subsystemList = this.subsystems() || [];
    const selected: number[] = [];

    for (const subsystem of subsystemList) {
      if (
        subsystem.identifier !== undefined &&
        this.parameterFormatService.isSubsystemVisible(subsystem.identifier, visibilityFlags)
      ) {
        selected.push(subsystem.identifier);
      }
    }

    return selected;
  });

  onSelectionChange(event: MatSelectChange) {
    const selectedIdentifiers: number[] = event.value || [];
    const newValue = this.calculateFlagsValue(selectedIdentifiers);
    this.parameterChange.emit({ config: this.config(), value: newValue });
  }

  private calculateFlagsValue(selectedIdentifiers: number[]): string {
    let flagsValue = 0;
    for (const identifier of selectedIdentifiers) {
      flagsValue |= 1 << identifier;
    }
    return flagsValue.toString();
  }

  onClickRemove() {
    this.parameterChange.emit({ config: this.config(), value: undefined });
  }

  get parameterTitle() {
    const { name, subcategory } = this.config()?.parameter || {};
    if (subcategory) return `${subcategory} / ${name}`;
    return name || 'Visibility Flags';
  }

  onClickConfigClose() {
    this.configClose.emit();
  }

  onClickUndo() {
    this.parameterUndo.emit(this.config());
  }

  onResetToFactory() {
    this.resetToFactory.emit(this.config());
  }
}
